<script setup>

import {computed, defineProps} from "vue";
import {formatAsCalendarDay} from "@/tools/date-time.tools";
import {usePayment} from "@/composables/payment";
import {useOrdersStore} from "@/store/orders";
import {useSettingsStore} from "@/store/settings";
import {openImagePreview} from "@/composables/image-preview";

const payment = usePayment()
const ordersStore = useOrdersStore();
const settingsStore = useSettingsStore();
const props = defineProps({
  order: { type: Object, required: true }
});

const orderDate = computed(() => {
  return formatAsCalendarDay(props.order.created_at)
})


const colorStatusClass = computed(() => {
  if (props.order.status === 'wait') {
    return "orderCard__statusRed";
  }
  return "orderCard__statusGreen";
})

const orderStatus = computed(() => {
  if (props.order.status === 'wait') {
    return "Ожидает оплаты";
  }
  return "Оплачен";
})

const totalSum = computed(() => {
  const itemsTotal = (props.order.order_items || []).reduce((acc, item) => (acc + item.price * item.qnt), 0);
  return itemsTotal + (props.order.use_delivery ? (settingsStore.deliveryCost || 0) : 0);
})


function paymentHandler() {
  ordersStore.setRecord(props.order)
  payment.handlePayment()
}


</script>

<template>
  <div class="orderCard">
    <div class="orderCard__line">
      <span class="orderCard__date">
        Заказ от {{orderDate}}
        <span class="orderCard__status" :class="colorStatusClass">
          {{ orderStatus }}
        </span>
      </span>
      <button
          class="tpay-button"
          v-if="order.status ==='wait'"
          @click="paymentHandler"
      >Оплатить</button>

    </div>
    <div>№ {{ order.order_number}}</div>
    <div>Товары</div>
    <div class="orderCard__products scroll-container">
      <div v-for="item in order.order_items" :key="item.id" class="orderCard__itemWrapper">
        <div>
          <img
              class="orderCard__image"
              :src="item.img_url || require('@/assets/img/preview_placeholder.jpg')"
              alt=""
              @click="openImagePreview(item.img_url)"
              @error="(e)=>e.target.src = require('@/assets/img/preview_placeholder.jpg')"
          />
          <div class="orderCard__itemName">
            {{ item.name }}
            <template v-if="item.series">&nbsp;серия {{ item.series }}</template>
            <template v-if="item.number">&nbsp;номер {{ item.number }}</template>
          </div>
        </div>
      </div>
    </div>
    <div class="orderCard__footerTotal">
      Итого
      <div class="orderCard__footerTotalSum">
        {{ totalSum }} ₽
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.orderCard {
  text-align: left;
  padding: 34px;
  border-radius: 20px;
  background-color: var(--cd-card-bg);
  display: flex;
  gap: 15px;
  flex-direction: column;
  &__products {
    width: 100%;
    height: auto;
    overflow-x: auto;
    overflow-y: hidden;
    margin-bottom: 10px;
    display: flex;
    gap: 8px;
    padding-bottom: 8px;
  }

  &__itemWrapper {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
  }

  &__itemWrapper > div {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__image {
    height: 100px;
    width: 116px;
    object-fit: cover;
    border-radius: 20px;
    border: 1px black;
    flex-shrink: 0;
    cursor: zoom-in;
  }
  &__itemName {
    width: 116px;
    font-size: 12px;
    color: var(--cd-text);
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    flex-shrink: 0;
    text-align: center;
  }
  &__coinTitle {
    font-size: 11px;
    color: #96E77D;
    margin-top: 2px;
  }
  &__status {
    font-family: var(--cd-default-font);
    font-weight: 400;
    font-size: 14px;
    padding: 3px 8px;
    border-radius: 22px;
  }
  &__statusRed {
    color: red;
    background: darksalmon;
  }

  &__statusGreen {
     color: #49E24C;
     background: #62C06442;
  }
  &__date {
    font-family: var(--cd-default-font);
    font-weight: 400;
    font-size: clamp(16px, 3vw, 26px);
    color: var(--cd-text);
  }
  &__line {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }
  &__footerTotalSum {
    color: #96E77D;
    font-size: 20px;
  }
}
.tpay-button {
  /* Фирменные цвета */
  background-color: var(--cd-pay-button-bg);
  color: #000000;

  /* Размеры и шрифты */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 50px; /* Высота Button L по гайдам */
  padding: 0 24px;
  border: none;
  border-radius: 100px; /* Фирменное скругление */

  /* Типографика */
  font-family: "TT Norms Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  text-decoration: none;
}

/* Эффект при наведении */
.tpay-button:hover {
  background-color: var(--cd-pay-button-hover);
}

/* Эффект при нажатии */
.tpay-button:active {
  background-color: var(--cd-pay-button-active);
  transform: scale(0.98);
}

/* Иконка (логотип) */
.tpay-logo {
  margin-right: 8px;
  font-weight: 900;
  font-style: italic;
}
</style>
